import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import CircularProgress from '@mui/material/CircularProgress';
import { processChequeStatusUpload } from './utils/utils';
import {
  ControlsRow,
  UploadButton,
  CancelIcon,
  ChooseFileButton,
  ChooseFileButtonWrapper,
  UploadProgressWrapper,
  UploadProgress,
  HelperTextWrapper
} from './styled';
import Icons from '../../components/icons';
import { PageWrapper, Title } from '../common/styled';

const ChequeStatusUpdate = () => {
  const [fileName, setFileName] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadAttempted, setUploadAttempted] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => setToastOpen(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFileName(event.target.files[0].name);
      setFile(event.target.files[0]);
      setUploadAttempted(false);
    } else {
      setFileName('');
      setFile(null);
    }
  };

  const handleRemoveFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileName('');
    setFile(null);
    setUploadAttempted(false);
  };

  const handleUpload = async () => {
    setUploadAttempted(true);
    if (!file || !fileName) {
      showToast('Please select a file to upload', SnackbarSeverityType.error);
      return;
    }
    try {
      setUploading(true);
      await processChequeStatusUpload(file, setUploadProgress, showToast);
    } catch (e: any) {
      const message = e || 'Upload failed';
      showToast(message, SnackbarSeverityType.error);
    } finally {
      setUploading(false);
    }
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current && !uploading) {
      fileInputRef.current.click();
    }
  };

  const showFieldError = uploadAttempted && !fileName;

  return (
    <Box>
      <Title>Import Cheque Status Report</Title>
      <PageWrapper>
        <Typography variant='h6' fontWeight={300} fontSize={12}>
          Browse Cheque Status Report{' '}
          <Box component='span' sx={{ color: 'red', display: 'inline' }}>
            *
          </Box>
        </Typography>
        <ControlsRow>
          <ChooseFileButtonWrapper>
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              accept='.csv'
              style={{ display: 'none' }}
            />
            <ChooseFileButton
              isFileName={!!fileName}
              onClick={handleChooseFileClick}
              disabled={uploading}
            >
              {uploading && (
                <UploadProgressWrapper>
                  <UploadProgress
                    uploadProgress={uploadProgress}
                  ></UploadProgress>
                </UploadProgressWrapper>
              )}

              {!fileName && <Icons.UploadFile />}
              {
                <Typography width={83} fontSize='16px !important'>
                  {fileName || 'Choose File'}
                </Typography>
              }
              {fileName && (
                <CancelIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                  disabled={uploading}
                >
                  <Icons.Close />
                </CancelIcon>
              )}
            </ChooseFileButton>
            <HelperTextWrapper>Please upload CSV file only</HelperTextWrapper>

            {showFieldError && (
              <Typography color='error' variant='caption'>
                This field is required.
              </Typography>
            )}
          </ChooseFileButtonWrapper>

          <UploadButton
            variant='contained'
            color='primary'
            onClick={handleUpload}
            disabled={!file}
          >
            {uploading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              'Upload'
            )}
          </UploadButton>
        </ControlsRow>
      </PageWrapper>
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ChequeStatusUpdate;
